import { useSyncExternalStore } from 'react';

/**
 * Installation de l'application sur l'appareil.
 *
 * Chrome et Edge (Android comme bureau) préviennent par l'événement `beforeinstallprompt` que le site remplit les
 * conditions d'installation, mais n'affichent plus de bannière d'eux-mêmes : sans bouton dans la page, personne ne
 * découvre qu'Ermont Live s'installe. On garde donc l'événement de côté pour le rejouer au clic.
 *
 * Safari (iOS) et Firefox n'implémentent pas cet événement : l'installation y passe par le menu de partage du
 * navigateur, d'où la marche à suivre affichée dans la page « À propos ».
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** À appeler une fois au démarrage, avant que l'événement ne soit émis. */
export function watchInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sans cela, le navigateur garde la main et choisit lui-même s'il propose quelque chose.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Vrai quand le navigateur accepte d'installer l'application maintenant. */
export function useCanInstall(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => deferred !== null,
    () => false,
  );
}

/** Vrai quand la page tourne déjà dans l'application installée. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches;
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (event === null) return 'unavailable';
  // Un événement ne se rejoue qu'une fois : on le retire avant même de connaître la réponse.
  deferred = null;
  notify();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}
